import type { Translations } from '../types';

const en = {
  absoluteCount: 'Absolute count',
  action: 'Action',
  actions: {
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
  },
  activityTime: 'Activity time',
  activityType: {
    eat: 'Food',
    entertainment: 'Entertainment',
    grocery: 'Grocery shopping',
    medical: 'Medical',
    outdoor: 'Outdoor',
    retail: 'Retail shopping',
    home: 'Going home',
    work: 'Work',
    others: 'Other',
  },
  additionalInfos: {
    activityTime:
      'A stay is identified when a person remains in the same location (about 100m) for 15 minutes or more within each hour.',
    ageGroup:
      "Age is estimated from mobile phone subscriber information (statistical data), and people under 20 are not included to protect minors' privacy.",
    arrival:
      'When only the origin is specified, trips within the same area (excursions) are automatically excluded.',
    customArea:
      'Custom area refers to an area that users can freely draw and set on the map, also known as "geofence".',
    departure:
      'When only the destination is specified, trips within the same area (excursions) are automatically excluded.',
    endActivityType:
      'Trip purpose is comprehensively estimated based on the arrival location, facility type, and stay duration.',
    excursion: 'Excursion refers to movement within the same area.',
    gender:
      'Gender is estimated from mobile phone subscriber information (statistical data).',
    gridHeatmap:
      'The grid corresponds to the standard regional mesh (from third-level mesh to 10m mesh).',
    home: 'Residence is determined as the location where the person stayed most frequently between 22:00 and 05:00 during the target period.',
    longestDistanceMode:
      'Mode of transportation is comprehensively estimated based on travel speed and transportation network information.',
    odMap:
      'OD Map is a map visualization that shows movement volume and stay volume by administrative area or regional mesh.',
    odMapCutoff: [
      'By default, trips whose counts are 10% or less of the maximum trip count are excluded for better visibility.',
      'Checking this will display all trips.',
    ],
    passageTime:
      'Passage time is determined as the time of entry into each road or route.',
    scale: [
      'Quantile is a relative classification divided by the top XX%, while Quantize is an absolute classification divided at equally spaced thresholds.',
      'Quantize is suitable for emphasizing outliers, while Quantile is suitable for visualizing data with small differences.',
      'And the number of colors may be lower than the specified one because colors are assigned based on the actual data distribution.',
    ],
    targetAgentCount: [
      'The number of people who match the selected analysis criteria.',
      'Each person is counted once, even if they match multiple times.',
    ],
    trafficVolume:
      'Traffic volume map is a map visualization that shows the amount of traffic on roads and routes by color.',
    tripPath:
      'Travel path map is a map visualization that connects origins and destinations with arcs.',
    visitor:
      'Visitors are people who arrived at the target area and are neither residents nor commuters.',
    walkingDistance: [
      'Walking area shows the reachable area based on the actual road network and an average adult walking speed (approximately 5 km/h).',
      'However, it is estimated comprehensively considering factors such as road type, slope, and presence of traffic signals.',
    ],
    work: 'Workplace is determined as a location where the person stayed for a total of 6 hours or more between 08:00 and 20:00 on weekdays during the target period (but not the residence).',
  },
  advancedAnalysis: 'Advanced analysis',
  ageGroup: 'Age group',
  ageGroups: {
    '10s': '10s',
    '20s': '20s',
    '30s': '30s',
    '40s': '40s',
    '50s': '50s',
    '60s': '60s',
    '60s+': 'Over 60s',
    '70s': '70s',
  },
  prefNames: {
    '01': 'Hokkaido',
    '02': 'Aomori',
    '03': 'Iwate',
    '04': 'Miyagi',
    '05': 'Akita',
    '06': 'Yamagata',
    '07': 'Fukushima',
    '08': 'Ibaraki',
    '09': 'Tochigi',
    '10': 'Gunma',
    '11': 'Saitama',
    '12': 'Chiba',
    '13': 'Tokyo',
    '14': 'Kanagawa',
    '15': 'Niigata',
    '16': 'Toyama',
    '17': 'Ishikawa',
    '18': 'Fukui',
    '19': 'Yamanashi',
    '20': 'Nagano',
    '21': 'Gifu',
    '22': 'Shizuoka',
    '23': 'Aichi',
    '24': 'Mie',
    '25': 'Shiga',
    '26': 'Kyoto',
    '27': 'Osaka',
    '28': 'Hyogo',
    '29': 'Nara',
    '30': 'Wakayama',
    '31': 'Tottori',
    '32': 'Shimane',
    '33': 'Okayama',
    '34': 'Hiroshima',
    '35': 'Yamaguchi',
    '36': 'Tokushima',
    '37': 'Kagawa',
    '38': 'Ehime',
    '39': 'Kochi',
    '40': 'Fukuoka',
    '41': 'Saga',
    '42': 'Nagasaki',
    '43': 'Kumamoto',
    '44': 'Oita',
    '45': 'Miyazaki',
    '46': 'Kagoshima',
    '47': 'Okinawa',
  },
  agentAnalysis: 'Agent analysis',
  agentCount: 'Agent count',
  agentInGrid: 'Agent count in each grid',
  aggregatedBy: 'Aggregated by this',
  aggregationTarget: 'Aggregation target',
  aggregationTargetByDimension: '{{target}} ({{dimension}})',
  aggregationTypes: {
    areaPref: 'Area (prefecture)',
    areas: 'Area (city, town)',
    arcs: 'Arcs',
    gridHeatmap: 'Grid heatmap',
    heatmap: 'Heatmap',
    links: 'Links',
    odMap: 'OD Map',
  },
  all: 'All',
  analysisResults: 'Analysis Results',
  analyzeOtherData: 'Analyze other prefecture data',
  and: 'and',
  apply: 'Apply',
  applyCondition: 'Apply condition',
  area: 'Area',
  areas: {
    city: 'City',
    state: 'State',
    town: 'Town',
  },
  arrivalTime: 'Arrival time',
  arrived: 'Arrived at {{place}}',
  barChartTitle: 'Comparison by {{target}}',
  bugfix: 'Bug fix',
  cancel: 'Cancel',
  checkAnalysis: 'Check analysis',
  circle: 'Circle',
  clearAll: 'Clear all',
  clickOrDropToUpload: 'Click or drag a file to upload.',
  close: 'Close',
  color: 'Color',
  compare: 'Compare',
  compareDataMultipleMaps: 'Compare maps on multiple data',
  compareMaps: 'Compare maps',
  convertToPolygon: 'Convert to polygon',
  copySharableUrlInClipboard: 'Copy current map sharing URL in the clipboard',
  countPerArea: 'Count per area',
  countries: {
    AUS: 'Australia',
    CAN: 'Canada',
    CHN: 'China',
    DEU: 'Germany',
    ESP: 'Spain',
    FRA: 'France',
    GBR: 'United Kingdom',
    HKG: 'Hong Kong',
    IDN: 'Indonesia',
    IND: 'India',
    ITA: 'Italy',
    JPN: 'Japan',
    KOR: 'Korea, Republic of',
    MYS: 'Malaysia',
    PHL: 'Philippines',
    RUS: 'Russian Federation',
    SGP: 'Singapore',
    THA: 'Thailand',
    TWN: 'Taiwan, Province of China',
    USA: 'United States',
    VNM: 'Viet Nam',
    ZZZ: 'Other',
  },
  create: 'Create',
  creatingCustomArea: 'Creating custom area ({{type}})',
  crossAnalysis: 'Cross analysis',
  csvDownload: 'Download CSV',
  csvDownloadUtf8: 'Download (UTF-8)',
  csvDownloadUtf8Bom: 'Download (UTF-8 BOM) for Excel',
  cumulative: 'Total',
  currentFilters: 'Current filters',
  custom: 'Custom',
  customArea: 'Custom area',
  customAreaName: 'Custom area name',
  dark: 'Dark',
  departed: 'Departed from {{place}}',
  departureTime: 'Departure time',
  discardChanges: 'Discard Changes',
  display: 'Display',
  displayAs: 'Display as',
  distribution: 'Distribution',
  done: 'Done',
  dontShowAgain: `Don't show this again`,
  download: 'Download',
  downloading: 'Downloading...',
  editMember: 'Edit member',
  editingCustomArea: 'Editing custom area ({{type}})',
  emailAddress: 'Email address',
  endActivity: 'End activity',
  endPlace: 'End place',
  error: 'Error',
  excursioned: 'Excursioned in {{place}}',
  expiry: 'Expiry',
  export: 'Export',
  feature: 'New feature',
  features: {
    resident: 'Residents',
    visitor: 'Visitors',
    worker: 'Commuters',
  },
  file: 'File',
  filter: 'Filter',
  filterBy: {
    features: 'Filter by person attributes',
    move: 'Filter by movement',
  },
  gender: 'Gender',
  genders: {
    male: 'Male',
    female: 'Female',
  },
  hide: 'Hide',
  holiday: 'Holiday',
  home: 'Home',
  importCustomArea: 'Import custom area',
  improvement: 'Improvement',
  instructions: 'Instructions',
  inviteMember: 'Invite member',
  invitedDatetime: 'Invited datetime',
  invitingMembers: 'Invited members',
  language: 'Language',
  lat: 'Lat',
  legend: 'Legend',
  light: 'Light',
  link: {
    highway: 'Highway',
    otherRoad: 'Other road',
    railway: 'Railway',
    trunkRoad: 'Trunk road',
  },
  list: 'List',
  listOthers: 'and {{count}} others',
  listDelimiter: ', ',
  load: 'Load',
  loading: 'Load',
  loadingNow: 'Loading...',
  login: 'Login',
  logout: 'Logout',
  longestDistanceMode: 'Means of transportation',
  lon: 'Lon',
  manageOrg: 'Manage organization',
  manual: 'Manual',
  map: 'Map',
  mean: 'Mean',
  median: 'Median',
  memberName: 'User name',
  mesh: 'Mesh',
  message: {
    analysisConditionApplied: 'Analysis condition "{{name}}" is applied',
    cancelingInvitation: 'Canceling invitation...',
    cannotDownload:
      'Download is available only when the target agent count is less than 300,000.',
    checkAnalysis:
      'Review the analysis results on maps and charts. Combine multiple conditions to analyze data from different perspectives.',
    confirmCancelInvitation:
      'Do you want to cancel the invitation for "{{email}}"?',
    confirmDeletion: 'Do you want to delete "{{name}}"?',
    confirmUserDeletion:
      'Do you want to delete user "{{userName}}" from the organization?',
    customAreaNameNotEntered: 'Please enter custom area name',
    customAreaNameDuplicate: 'A custom area with the same name already exists.',
    circleCustomAreaCreatingInstructions: [
      'Click on the map to set the center point.',
      'Click on another point to set the radius and complete the circle.',
    ],
    circleCustomAreaEditingInstructions: [
      'Drag to move the circle.',
      'Changing the radius will automatically update the size of the circle.',
    ],
    polygonCustomAreaCreatingInstructions: [
      'Click on the map to set the first vertex.',
      'Click on the subsequent vertices sequentially to create the polygon.',
      'Press the Enter key or click the first vertex to complete the polygon.',
    ],
    polygonCustomAreaEditingInstructions: [
      'Drag to move the polygon.',
      'Drag the vertices to edit the shape of the polygon.',
      'Hold the Ctrl key or Command key and click on a vertex to delete it.',
      'Hold the Ctrl key or Command key and click on an edge to add a vertex.',
    ],
    walkingCustomAreaCreatingInstructions: [
      'Click on the map to set the starting point.',
      'A custom area will be created automatically based on walking minutes.',
    ],
    walkingCustomAreaEditingInstructions: [
      'Changing walking minutes will automatically update the shape of the custom area.',
    ],
    deletingMember: 'Deleting member...',
    downloadAnalysisResult: 'Download analysis result in CSV format',
    downloadCancelled:
      'Download was canceled as the condition has been changed',
    emailInvalid: 'Please enter valid email address',
    emailRequired: 'Please enter email address',
    failedToCancelInvitation: 'Failed to cancel invitation',
    failedToDelete: 'Failed to delete',
    failedToDeleteMember: 'Failed to delete member',
    failedToInviteMember: 'Failed to invite member',
    failedToSaveCustomArea: 'Failed to save custom area.',
    failedToUpdateMember: 'Failed to update member information',
    failedToUpdateName: 'Failed to update name',
    failedToUploadFile: 'Failed to upload file. Please check the file format.',
    fileTooLarge:
      'File size is too large. Please select a file smaller than {{max}}MB.',
    invalidRadius: 'Radius should be between {{min}}km and {{max}}km.',
    invalidWalkingMins:
      'Walking minutes should be between {{min}} minutes and {{max}} minutes.',
    invitationCancelled: 'Canceled the invitation successfully',
    invitingMember: 'Inviting member...',
    memberDeleted: 'Deleted the member successfully',
    memberInfoCannotBeModified:
      'Email address and role cannot be updated once member is created. You need to delete the member and create new member if you wish to update the information.',
    memberInvited: 'Invited the member successfully',
    memberUpdated: 'Updated member information successfully',
    nameChanged: 'Name changed from "{{from}}" to "{{to}}"',
    nameRequired: 'Please enter user name',
    newBadge: {
      customArea:
        'Custom area editing, renaming, saving, and deleting have been consolidated in the UI. Additionally, the UI has been improved for easier editing.',
      colorPalette:
        'You can now change the color palette of the map. Please use it according to your preference.',
    },
    noAgentFound: 'No Agent found for the condition',
    noAggregationTarget: 'No aggregation target specified.',
    noCustomAreaDisplayed: 'No custom area displayed.',
    noCustomAreaFound: 'No custom area found for the condition.',
    noDataFound: 'No data found for the condition',
    noTripFound: 'No Trip found for the condition',
    noFiltersApplied: 'No filters are applied',
    odMapUnavailable:
      'OD Map is not available because departure, arrival or pass-through links are not set.',
    operationCannotBeCancelled: 'This operation cannot be canceled',
    passageTimeUnavailableDueToConditions:
      'Passage time cannot be displayed because pass-through links are not set as filter conditions.',
    savedCustomArea: 'Custom area "{{name}}" has been saved.',
    selectArea:
      'Click the area you want to analyze on the map. Hold down the Shift key while clicking to select multiple areas at the same time. You can also create a custom area to specify any range.',
    selectAnalysisCondition: 'Select analysis conditions',
    selectToCompare: 'Please select a comparison',
    setFilters:
      'Set analysis conditions for the selected area. They are reflected on the target agents count and the map.',
    shareLinkCopied1: 'The share link has been copied in the clipboard.',
    shareLinkCopied2: 'Please send the URL to share the analysis result.',
    startAnalysis: `Let's start the analysis.`,
    stayingPopulationUnavailableDueToConditions:
      'Staying population cannot be displayed because departure or pass-through points are set as filter conditions.',
    supportedFileFormats:
      'Currently supported file formats are GeoJSON or WKT polygons only. "MultiPolygon" and "FeatureCollection" are treated as separate polygons. Holes in polygons are ignored.',
    unexpectedError: 'Unexpected error occurred',
    updatingMember: 'Updating member information...',
    welcome: 'Welcome to <logo />.',
  },
  moveToThere: 'Move to the point',
  nationality: 'Nationality',
  newTab: 'New tab',
  notice: 'Notice',
  notification: 'Notification',
  odMap: 'OD Map',
  odMapType: 'OD Map type',
  opacity: 'Opacity',
  openInNewTab: 'Open in new tab',
  openMapInNewTab: 'Open the current map in a new tab',
  or: 'or',
  orgMemberList: 'Member list in organization',
  pass: 'Pass',
  passageTime: 'Passage time',
  passed: 'Passed through {{place}}',
  polygon: 'Polygon',
  quantile: 'Quantile',
  quantize: 'Quantize',
  radius: 'Radius',
  rename: 'Rename',
  roadStreet: 'Road/Route',
  role: 'Role',
  roleAdmin: 'Administrator',
  roleMember: 'Member',
  save: 'Save',
  saveArea: 'Save area',
  saved: 'Saved',
  scale: 'Scale',
  searchCustomArea: 'Search custom areas',
  searchOnMap: 'Search on map',
  selectArea: 'Select area',
  selectCustomArea: 'Select custom area',
  selectData: 'Select data',
  selectCustomizedData: 'Select customized GAD data',
  selectGadAllJapanData: 'Select target month and region',
  selectingMultipleAreas: 'Selecting {{count}} areas',
  selectingMultipleLinks: 'Selecting {{count}} roads/routes',
  setFilters: 'Set filters',
  share: 'Share',
  showAgain: 'Show again',
  showAllTrips: 'Show all trips',
  showCustomData: 'Show custom GAD',
  startingGuide: 'User guide',
  startPlace: 'Start place',
  startSelectingExcursionArea: 'Select excursion area',
  startWithoutArea: 'Start analysis without specifying an area',
  stayingPopulation: 'Staying population',
  stayingTime: 'Staying time',
  stayingTimeRange: 'Staying time range',
  stopBy: 'Stop by',
  stopByAfter: 'Stop by after',
  stopByAll: 'Stop by all',
  stopByBefore: 'Stop by before',
  stopSelectingExcursionArea: 'Stop selecting excursion area',
  submit: 'Register',
  summary: 'Summary',
  targetAgentCount: 'Target agent count',
  theme: 'Theme',
  theseAreas: 'these areas',
  theseLinks: 'these roads/routes',
  traffic: 'Traffic',
  traveledDistance: 'Travel distance',
  travelMode: {
    airplane: 'Airplane',
    bike: 'Bicycle',
    car: 'Car',
    bus: 'Bus',
    rail: 'Train',
    ship: 'Ship',
    walk: 'Walk',
    other: 'Others',
  },
  traveledTime: 'Travel time',
  tripAnalysis: 'Trip analysis',
  tripCounts: 'Trip counts',
  tripEnd: 'Trip end',
  tripExcursion: 'Trip excursion',
  tripPass: 'Trip pass',
  tripPath: 'Trip path',
  tripStart: 'Trip start',
  undo: 'Undo',
  unit: {
    agent: '',
    agentPerArea: '/km²',
    car: '',
    mins: 'minutes',
  },
  unknown: 'Unknown',
  visit: 'Visited place',
  visualizeInThreeDMode: 'Visualize in 3D',
  walkingDistance: 'Walking area',
  weekday: 'Weekday',
  work: 'Work',
  yearAndMonth: '{{year}} / {{month}}',
  zoom: 'Zoom',
} as const satisfies Translations;

export { en };
